import { useState, useEffect, useContext } from "react";
import { Stack, Link as A, Button, Alert, Typography} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Link, useNavigate } from "react-router-dom";
import API from "../../../services/API";
import useTester from "../../../services/Hooks/useTester";
import General from "./General";
import Pro from "./Pro";
import Terms from "./Terms";
import Steps from "../../Tools/Steps";
import Location from "./Location";
import randomInt from "../../../services/randomInt";
import useJsonStore from "../../../services/Hooks/useJsonStore";
import Tracker from "../../../services/Tracker"
import JWT from "../../../services/JWT";
import targetPath from "../../../services/targetPath";
import { txt } from "../../../services/translation";
import UserContext from "../../../services/Context/UserContext";

export default function SignUpForm({ successUrl, signInUrl }){

    const [register, setRegister] = useState(null)
    const [loading, setLoading] = useState(false)
    const [errors, setErrors] = useState({})
    const [step, setStep] = useState(0)
    const [verificationCode, setVerificationCode] = useState(null)
    const [studentFile, setStudentFile] = useState(null)

    const { setUser } = useContext(UserContext) 

    const navigate = useNavigate()

    const tester = useTester()

    const store = useJsonStore();

    useEffect(() => {

        const params = new URLSearchParams(window.location.search);

        let newUser = store.get('register') || {
            phone: '+33',
            terms: false,
            handicap: false,
            newsletter: false,
            country: 'FR',
            emailVerified: false,
            email: (params.get('email') || null),
            rpps: (params.get('rpps') || null),
            siret: (params.get('siret') || null),
            category: (params.get('category') ? { id: params.get('category') } : null),
            status: (params.get('status') ? { id: params.get('status') } : null)
        };

        setVerificationCode( randomInt(1000, 9999).toString() )

        setRegister(newUser)

    }, [setRegister, store])

    const stepIs = s => step === s


    const handleSubmit = e => {
        e.preventDefault();

        if( !validate() ) return false;

        setLoading(true);

        store.set('register', register);

        if(stepIs(0)) return handleGeneral();

        if(stepIs(1)) return handlePro();
    
        if(stepIs(2)) return nextStep();

        return API.post('/register', register)
            .then(r => handleSuccess(r.data))
            .catch(e => handleError(e))
        
    }


    const validate = () => {
        let e = {};
    
        if(stepIs(0)){
            if(!tester.testPassword(register.password)) 
                e.password = txt('passwordValidationMessage');
            if(!tester.testPhone(register.phone)) 
                e.phone = txt('phoneValidationMessage');
            if(!tester.testEmail(register.email)) 
                e.email = txt('emailValidationMessage');
        }
    
        if(stepIs(1) && register.status?.id === 'etudiant' && !studentFile && !register.studentProof)
            e.studentFile = txt('studentFileRequired');
    
        if(stepIs(3) && !register.terms) 
            e.terms = txt('termsAcceptanceRequired');
        
        setErrors(e);
    
        return Object.keys(e).length === 0;
    }


    const nextStep = () => {
        setLoading(false)
        setStep(step + 1)
    }


    const handleSuccess = data => {

        console.log('REGISTER RESULT', register, data);
        
        store.remove('register');

        Tracker.trackRegistration(data);

        setUser(data);

        JWT.setTokens(data);

        navigate(successUrl || targetPath());
    }

    const handleError = e => {
        console.log(e?.response?.data);
        setLoading(false);
        setErrors({api: txt('generalApiErrorMessage')});
    }

    const handleGeneral = () => {

        let emailCheckPath = `/check-email?email=${register.email}`;
        emailCheckPath += !register.emailVerified ? `&code=${verificationCode}` : '';

        return API.get(emailCheckPath)
            .then(r => nextStep())
            .catch(e => handleEmailError(e))
    }

    const handleEmailError = e => {
        console.log(e.response?.data);
        setLoading(false);
        setErrors({email: 
            e.response?.status === 409 ? txt('emailAlreadyUsedError') : txt('invalidEmailAddressError')
        });
    }

    const handlePro = () => {

        if(!register.prospect) createProspect();

        if(studentFile && !register.studentProof){

            return API.upload('/student-proof', studentFile)
                .then(r => handleStudentSuccess(r.data))
                .catch(e => handleStudentError(e))
        }

        return nextStep();
    }

    const handleStudentSuccess = data => {
        console.log('student file success', data);
        let newUser = {...register, studentProof: data};
        setRegister(newUser);
        store.set('register', newUser);
        nextStep();
    }

    const handleStudentError = e => {
        console.log(e?.response?.data);
        setLoading(false);
        setErrors({...errors, studentFile: txt('invalidFileErrorMessage')});
    }

    const createProspect = () => {
        let prospect = {...register, 
            entryPoint: window.sessionStorage.getItem('targetPath') || null
        };

        API.post('/prospects', prospect)
            .then(r => setRegister({...register, prospect: r.data}))
            .catch(e => console.log(e?.response?.data))
    }

    return register &&

        <form onSubmit={handleSubmit}>

            <Stack direction='column' spacing={3} sx={{maxWidth: 600, margin: 'auto'}}>

                <Typography variant='h1' component='h1' sx={{pb: 1}}>
                    {txt('createYourAccount')}
                </Typography>

                <Typography>
                    {txt('alreadyHaveAccount')} <Link to={signInUrl || '/login'}><A component='span'>{txt('logIn')}</A></Link>
                </Typography>

                <Steps 
                    length={4}
                    value={step}
                    onChange={step => setStep(step)}
                />

                {errors.api && 
                    <Alert severity='error'>{errors.api}</Alert>
                }

                <General 
                    user={register}
                    onChange={register => setRegister(register)}
                    errors={errors}
                    show={stepIs(0)}
                />

                <Pro 
                    user={register}
                    onChange={register => setRegister(register)}
                    errors={errors}
                    show={stepIs(1)}
                    onStudentFileChange={file => setStudentFile(file)}
                    studentFile={studentFile}
                />

                <Location
                    user={register}
                    onChange={register => setRegister(register)}
                    show={stepIs(2)}
                />

                <Terms
                    user={register}
                    onChange={register => setRegister(register)}
                    errors={errors}
                    show={stepIs(3)}
                />

                <Stack direction='row' justifyContent='space-between'>

                    {!stepIs(0) ? 
                        <Button 
                            variant='outlined'
                            onClick={() => setStep(step - 1)}
                        >
                            {txt('previous')}
                        </Button> 
                    : <div></div>}

                    <LoadingButton
                        type='submit'
                        loading={loading}
                        variant='contained'
                    >
                        {stepIs(3) ? txt('confirm') : txt('continue')}
                    </LoadingButton>
                    
                </Stack>

            </Stack>

        </form>
}